<script setup lang="ts">
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Title } from '@/components/ui/title'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head, Link } from '@inertiajs/vue3'
import { ArrowLeftCircle } from 'lucide-vue-next'
import { computed } from 'vue'

export interface ImportActivityLog {
    id: number
    created_at: string
    body: Record<string, unknown>
    metadata: Record<string, unknown>
}

export interface AdminUserShow {
    id: number
    name: string | null
    email: string
    sso_user_id: number | null
    created_at: string
}

const props = defineProps<{
    user: AdminUserShow
    activity_logs: ImportActivityLog[]
}>()

function prettyJson(obj: unknown) {
    try {
        return JSON.stringify(obj, null, 2)
    } catch {
        return String(obj)
    }
}

function statusBadgeClass(status: unknown) {
    const s = String(status || '')
    if (s === 'success') return 'border-emerald-200 bg-emerald-50 text-emerald-900'
    if (s === 'error') return 'border-red-200 bg-red-50 text-red-900'
    return 'border-slate-200 bg-slate-100 text-slate-700'
}

const hasLogs = computed(() => props.activity_logs.length > 0)
</script>

<template>
    <Head :title="`Logs — ${user.name || user.email}`" />

    <LightLayout contentClasses="bg-slate-50">
        <main class="container flex max-w-4xl flex-col gap-8">
            <Title class="flex items-center gap-2 font-sans font-semibold">
                <Link href="/admin/users"> <ArrowLeftCircle class="h-8 w-8" stroke-width="1.2" /> </Link>
                {{ user.name || user.email }}
            </Title>

            <Card class="border-0 shadow-sm">
                <CardContent class="space-y-3 pt-6">
                    <div class="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" class="border-cyan-200 bg-cyan-50 text-cyan-900">Usuário</Badge>
                    </div>
                    <p class="text-sm text-slate-600">
                        <span class="font-medium text-slate-800">E-mail:</span>
                        {{ user.email }}
                    </p>
                    <p v-if="user.sso_user_id != null" class="text-sm text-slate-600">
                        <span class="font-medium text-slate-800">ID SSO (CAS):</span>
                        <code class="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-sm">{{ user.sso_user_id }}</code>
                    </p>
                </CardContent>
            </Card>

            <section class="space-y-4">
                <div>
                    <h2 class="text-lg font-semibold text-slate-900">Logs de importação</h2>
                    <p class="text-muted-foreground text-sm">Resposta do CAS por execução do job (mais recente primeiro).</p>
                </div>

                <div
                    v-if="!hasLogs"
                    class="text-muted-foreground rounded-xl border border-dashed border-slate-200 bg-slate-50/80 py-14 text-center text-sm"
                >
                    Nenhum log de importação para este usuário.
                </div>

                <ul v-else class="space-y-6">
                    <li v-for="log in activity_logs" :key="log.id">
                        <Card class="overflow-hidden border-slate-200/80 border-l-4 border-l-cyan-500 shadow-md">
                            <CardContent class="space-y-4 p-5">
                                <div class="flex flex-wrap items-start justify-between gap-3">
                                    <div class="flex flex-wrap items-center gap-2">
                                        <time
                                            class="text-sm font-semibold tabular-nums text-slate-900"
                                            :datetime="log.created_at"
                                        >
                                            {{ new Date(log.created_at).toLocaleString('pt-BR') }}
                                        </time>
                                        <Badge
                                            v-if="log.body.status != null"
                                            variant="outline"
                                            :class="statusBadgeClass(log.body.status)"
                                            class="text-xs font-semibold uppercase tracking-wide"
                                        >
                                            {{ log.body.status }}
                                        </Badge>
                                        <Badge
                                            v-if="'new_user' in log.body"
                                            variant="secondary"
                                            class="rounded-full text-xs"
                                        >
                                            {{ log.body.new_user ? 'Novo no CAS' : 'Atualizado no CAS' }}
                                        </Badge>
                                    </div>
                                    <span v-if="log.body.id != null" class="text-muted-foreground text-xs">
                                        CAS user id:
                                        <code class="rounded bg-slate-100 px-1 py-0.5 font-mono text-slate-800">{{ log.body.id }}</code>
                                    </span>
                                </div>

                                <div
                                    v-if="Array.isArray(log.body.errors) && log.body.errors.length"
                                    class="rounded-lg border border-red-200 bg-red-50/80 p-3 text-sm text-red-900"
                                >
                                    <p class="font-semibold">Erros</p>
                                    <ul class="mt-1 list-disc pl-5">
                                        <li v-for="(err, i) in log.body.errors" :key="i">{{ err }}</li>
                                    </ul>
                                </div>

                                <details v-if="log.metadata && Object.keys(log.metadata).length" class="group">
                                    <summary
                                        class="cursor-pointer text-sm font-semibold text-cyan-700 hover:underline group-open:text-cyan-900"
                                    >
                                        Dados sincronizados (metadata)
                                    </summary>
                                    <pre
                                        class="mt-2 max-h-72 overflow-auto rounded-lg border border-slate-800 bg-[#1e1e2e] p-4 text-xs leading-relaxed text-[#cdd6f4]"
                                        tabindex="0"
                                        >{{ prettyJson(log.metadata) }}</pre
                                    >
                                </details>

                                <details class="group" open>
                                    <summary
                                        class="cursor-pointer text-sm font-semibold text-cyan-700 hover:underline group-open:text-cyan-900"
                                    >
                                        Resposta completa (body)
                                    </summary>
                                    <pre
                                        class="mt-2 max-h-80 overflow-auto rounded-lg border border-slate-800 bg-[#1e1e2e] p-4 text-xs leading-relaxed text-[#cdd6f4]"
                                        tabindex="0"
                                        >{{ prettyJson(log.body) }}</pre
                                    >
                                </details>
                            </CardContent>
                        </Card>
                    </li>
                </ul>
            </section>

            <div class="flex flex-wrap gap-3 pb-8">
                <Button variant="outline" :as="Link" href="/admin/users"> ← Lista de usuários </Button>
                <Button variant="ghost" :as="Link" href="/"> Página inicial </Button>
            </div>
        </main>
    </LightLayout>
</template>
